import { createContext, useCallback, useContext, useEffect } from 'react';
import { StoreApi, create, useStore } from 'zustand';
import { Product } from '../api/contracts/catalog/types';
import { FinanceModel, PeripheralFinanceModel } from '../api/contracts/financemodels';
import { useProducts } from '../api/hooks/product';
import { useBundle } from '../bundles/useBundleService';

export interface SelectedFinanceModel {
  financeCategoryId: string;
  modelId: string;
}

export interface StockBundleWeighting {
  id?: string;
  bundleId: string;
  weightingPercentage: number;
}

export interface AdditionalStock {
  id?: string;
  productId: string;
  minimumStockLevel: number;
  maximumStockLevel: number;
}

export interface StockParameters {
  numberOfEmployees?: number;
  churnRatePercentage?: number;
  gsdPercentage?: number;
}

export enum StockLevelStatus {
  NONE = 0,
  DRAFT = 1,
  AWAITING_APPROVAL = 2,
  APPROVED = 3
}

export interface CustomerCatalogueInfo {
  selectedProductIds: string[];
  selectProduct: (productId: string) => void;
  selectedFinanceModels: FinanceModel[];
  selectedPeripheralFinanceModel?: PeripheralFinanceModel;
  selectFinanceModel: (model: FinanceModel) => Promise<void>;
  selectPeripheralFinanceModel: (model: PeripheralFinanceModel) => Promise<void>;
  selectCapitalPurchaseForMainDevice: (categoryId: string, manufacturerId: string) => Promise<void>;
  selectCapitalPurchaseForPeripheral: () => Promise<void>;
  stockParameters: StockParameters;
  bundleWeightings: StockBundleWeighting[];
  additionalBundleStockLevels: AdditionalStock[];
  additionalGeneralStockLevels: AdditionalStock[];
  setStockParameters: (newParams: StockParameters) => void;
  saveStockParameters: (submit?: boolean) => Promise<void>;
  setBundleWeighting: (bundleId: string, weighting?: number) => void;
  saveBundleWeighting: (bundleId: string) => Promise<void>;
  setBundleWeightings: (newArray: StockBundleWeighting[]) => void;
  setAdditionalBundleStock: (productId: string, minimumStockLevel: number, maximumStockLevel: number) => void;
  saveAdditionalBundleStock: (productIds: string[]) => Promise<void>;
  setAdditionalBundleStockLevels: (newArray: AdditionalStock[]) => void;
  setAdditionalGeneralStock: (productId: string, minimumStockLevel: number, maximumStockLevel: number) => void;
  saveAdditionalGeneralStock: (productIds: string[]) => Promise<void>;
  setAdditionalGeneralStockLevels: (newArray: AdditionalStock[]) => void;
  status: StockLevelStatus;
  setStatus: (newStatus: StockLevelStatus) => void;
}

export interface CustomerCatalogueProps {
  customerId: string;
  customPartnerId?: string;
}

export const CustomerCatalogueContext = createContext<StoreApi<CustomerCatalogueInfo>>(
  create<CustomerCatalogueInfo>(() => ({
    selectedProductIds: [],
    selectProduct: () => {},
    selectedFinanceModels: [],
    selectedPeripheralModel: undefined,
    selectFinanceModel: async () => {},
    selectPeripheralFinanceModel: async () => {},
    selectCapitalPurchaseForMainDevice: async () => {},
    selectCapitalPurchaseForPeripheral: async () => {},
    stockParameters: {
      numberOfEmployees: 0,
      churnRatePercentage: 0,
      gsdPercentage: 0
    },
    bundleWeightings: [],
    additionalBundleStockLevels: [],
    additionalGeneralStockLevels: [],
    setStockParameters: () => {},
    saveStockParameters: async () => {},
    setBundleWeighting: () => {},
    saveBundleWeighting: async () => {},
    setBundleWeightings: () => {},
    setAdditionalBundleStock: () => {},
    saveAdditionalBundleStock: async () => {},
    setAdditionalBundleStockLevels: () => {},
    setAdditionalGeneralStock: () => {},
    saveAdditionalGeneralStock: async () => {},
    setAdditionalGeneralStockLevels: () => {},
    status: StockLevelStatus.DRAFT,
    setStatus: () => {}
  }))
);

function useCustomerCatalogueContext<U>(selector: (store: CustomerCatalogueInfo) => U): U | undefined {
  const context = useContext(CustomerCatalogueContext);
  if (!context) {
    throw new Error('useCustomerCatalogue must be used within a CustomerCatalogue provider');
  }
  return useStore(context, selector);
}

export const useSelectedProductIds = () => {
  return useCustomerCatalogueContext(state => state.selectedProductIds) || [];
};

export const useSelectProduct = (product: Product) => {
  const selectId = useCustomerCatalogueContext(state => state.selectProduct);

  if (!selectId) {
    throw new Error('Missing product id');
  }

  return useCallback(() => selectId(product.id), [selectId, product]);
};

export const useIsProductSelected = (product: Product) => {
  const ids = useSelectedProductIds();

  return ids.includes(product.id);
};

export const useSelectedProducts = () => {
  const [products] = useProducts();
  const selectedProductIds = useSelectedProductIds();

  return products.filter(p => selectedProductIds.includes(p.id));
};

export const useBundledProducts = () => {
  const [products] = useProducts();
  const bundles = useBundle(s => s.bundles);
  const bundledProductIds = Array.from(
    new Set(bundles.map(bundle => [bundle.mainDevice, ...bundle.bundledItems.map(item => item.catalogItem)]).flat())
  );

  return products.filter(p => bundledProductIds.includes(p.id));
};

export const useSelectedFinanceModel = (manufacturerId: string, categoryId: string) => {
  const selectedModels = useCustomerCatalogueContext(state => state.selectedFinanceModels);
  const selectedModel = selectedModels?.find(m => m.categoryId === categoryId && m.manufacturerId === manufacturerId);

  return selectedModel;
};

export const useSelectedPeripheralFinanceModel = () => {
  const selectedModel = useCustomerCatalogueContext(state => state.selectedPeripheralFinanceModel);

  return selectedModel;
};

export const useAllSelectedFinanceModels = () => {
  const selectedModels = useCustomerCatalogueContext(state => state.selectedFinanceModels);

  return selectedModels;
};

export const useSelectFinanceModel = () => {
  const select = useCustomerCatalogueContext(state => state.selectFinanceModel);

  if (!select) {
    throw Error('useSelectFinanceModel must be used within a CustomerCatalogueContextProvider');
  }

  return select;
};

export const useSelectPeripheralFinanceModel = () => {
  const select = useCustomerCatalogueContext(state => state.selectPeripheralFinanceModel);

  if (!select) {
    throw Error('useSelectPeripheralFinanceModel must be used within a CustomerCatalogueContextProvider');
  }

  return select;
};

export const useSelectCapitalPurchaseForMainDevice = () => {
  const select = useCustomerCatalogueContext(state => state.selectCapitalPurchaseForMainDevice);

  if (!select) {
    throw Error('useSelectCapitalPurchaseForMainDevice must be used within a CustomerCatalogueContextProvider');
  }

  return select;
};

export const useSelectCapitalPurchaseForPeripheral = () => {
  const select = useCustomerCatalogueContext(state => state.selectCapitalPurchaseForPeripheral);

  if (!select) {
    throw Error('useSelectCapitalPurchaseForPeripheral must be used within a CustomerCatalogueContextProvider');
  }

  return select;
};

export const useStockParameters = () => {
  const stockParameters = useCustomerCatalogueContext(state => state.stockParameters);
  const bundleWeightings = useCustomerCatalogueContext(state => state.bundleWeightings);

  const bundles = useBundle(s => s.bundles);
  const setBundleWeightings = useCustomerCatalogueContext(state => state.setBundleWeightings);

  if (!stockParameters || !bundleWeightings || !setBundleWeightings) {
    throw Error('useStockParameters must be used in a CustomerCatalogueProvider');
  }

  useEffect(() => {
    let updateRequired = false;
    const newWeightings = [...bundleWeightings].filter(n => !!bundles.find(b => b.id === n.bundleId));

    if (newWeightings.length !== bundleWeightings.length) {
      updateRequired = true;
    }

    bundles.forEach(bundle => {
      const weighting = newWeightings.find(n => n.bundleId === bundle.id);

      if (!weighting) {
        newWeightings.push({ bundleId: bundle.id, weightingPercentage: 0 });
        updateRequired = true;
      }
    });

    if (updateRequired) {
      setBundleWeightings(newWeightings);
    }
  }, [bundles, setBundleWeightings, bundleWeightings]);

  return { stockParameters, bundleWeightings };
};

export const useSetStockParameters = () => {
  const setStockParameters = useCustomerCatalogueContext(state => state.setStockParameters);
  const setBundleWeighting = useCustomerCatalogueContext(state => state.setBundleWeighting);

  if (!setStockParameters || !setBundleWeighting) {
    throw Error('useSetStockParameters must be used in a CustomerCatalogueProvider');
  }

  return { setStockParameters, setBundleWeighting };
};

export const useSaveStockParameters = () => {
  const saveStockParameters = useCustomerCatalogueContext(state => state.saveStockParameters);

  if (!saveStockParameters) {
    throw Error('useSaveStockParameters must be used in a CustomerCatalogueProvider');
  }

  return saveStockParameters;
};

export const useSaveBundleWeighting = () => {
  const saveBundleWeighting = useCustomerCatalogueContext(state => state.saveBundleWeighting);

  if (!saveBundleWeighting) {
    throw Error('useSaveBundleWeighting must be used in a CustomerCatalogueProvider');
  }

  return saveBundleWeighting;
};

export const useAdditionalBundleStockLevels = () => {
  const stockLevels = useCustomerCatalogueContext(state => state.additionalBundleStockLevels);

  const selectedProducts = useSelectedProducts();
  const setStockLevels = useCustomerCatalogueContext(state => state.setAdditionalBundleStockLevels);

  if (!stockLevels || !selectedProducts || !setStockLevels) {
    throw Error('useAdditionalBundleStockLevels must be used in a CustomerCatalogueProvider');
  }

  useEffect(() => {
    let updateRequired = false;
    const newLevels = [...stockLevels].filter(n => !!selectedProducts.find(p => p.id === n.productId));

    if (newLevels.length !== stockLevels.length) {
      updateRequired = true;
    }

    selectedProducts.forEach(product => {
      const level = newLevels.find(n => n.productId === product.id);

      if (!level) {
        newLevels.push({ productId: product.id, minimumStockLevel: 0, maximumStockLevel: 0 });
        updateRequired = true;
      }
    });

    if (updateRequired) {
      setStockLevels(newLevels);
    }
  }, [stockLevels, selectedProducts, setStockLevels]);

  return stockLevels;
};

export const useAdditionalGeneralStockLevels = () => {
  const stockLevels = useCustomerCatalogueContext(state => state.additionalGeneralStockLevels);

  const selectedProducts = useSelectedProducts();
  const setStockLevels = useCustomerCatalogueContext(state => state.setAdditionalGeneralStockLevels);

  if (!stockLevels || !selectedProducts || !setStockLevels) {
    throw Error('useAdditionalGeneralStockLevels must be used in a CustomerCatalogueProvider');
  }

  useEffect(() => {
    let updateRequired = false;
    const newLevels = [...stockLevels].filter(n => !!selectedProducts.find(p => p.id === n.productId));

    if (newLevels.length !== stockLevels.length) {
      updateRequired = true;
    }

    selectedProducts.forEach(product => {
      const level = newLevels.find(n => n.productId === product.id);

      if (!level) {
        newLevels.push({ productId: product.id, minimumStockLevel: 0, maximumStockLevel: 0 });
        updateRequired = true;
      }
    });

    if (updateRequired) {
      setStockLevels(newLevels);
    }
  }, [stockLevels, selectedProducts, setStockLevels]);

  return stockLevels;
};

export const useSetAdditionalBundleStockLevel = () => {
  const setAdditionalStockLevel = useCustomerCatalogueContext(state => state.setAdditionalBundleStock);

  if (!setAdditionalStockLevel) {
    throw Error('useSetAdditionalBundleStockLevel must be used in a CustomerCatalogueProvider');
  }

  return setAdditionalStockLevel;
};

export const useSetAdditionalGeneralStockLevel = () => {
  const setAdditionalStockLevel = useCustomerCatalogueContext(state => state.setAdditionalGeneralStock);

  if (!setAdditionalStockLevel) {
    throw Error('useSetAdditionalGeneralStockLevel must be used in a CustomerCatalogueProvider');
  }

  return setAdditionalStockLevel;
};

export const useSaveAdditionalBundleStockLevel = () => {
  const saveAdditionalStockLevel = useCustomerCatalogueContext(state => state.saveAdditionalBundleStock);

  if (!saveAdditionalStockLevel) {
    throw Error('useSaveAdditionalStockLevels must be used in a CustomerCatalogueProvider');
  }

  return saveAdditionalStockLevel;
};

export const useSaveAdditionalGeneralStockLevel = () => {
  const saveAdditionalStockLevel = useCustomerCatalogueContext(state => state.saveAdditionalGeneralStock);

  if (!saveAdditionalStockLevel) {
    throw Error('useSaveAdditionalGeneralStockLevel must be used in a CustomerCatalogueProvider');
  }

  return saveAdditionalStockLevel;
};

export const useStockLevelStatus = () => {
  const status = useCustomerCatalogueContext(state => state.status);

  if (status === undefined) {
    throw Error('useStockLevelStatus must be used in a CustomerCatalogueProvider');
  }

  return status;
};

export const useSubmitStockLevels = () => {
  const saveStockParameters = useCustomerCatalogueContext(state => state.saveStockParameters);

  if (!saveStockParameters) {
    throw Error('useSubmitStockLevels must be used in a CustomerCatalogueProvider');
  }

  return useCallback(async () => {
    return await saveStockParameters(true);
  }, [saveStockParameters]);
};
